import React, { ComponentType, useCallback, useMemo, useState } from "react";

import type { Dayjs } from "dayjs";

import KpiCardJs from "../../shared/Dashboard/KpiCard/KpiCard";
import DatePickerJs from "../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";

import DailyPerformance from "./DailyPerformance/DailyPerformance";
import RedeemableBalance from "./RedeemableBalance/RedeemableBalance";
import DepositByPlatform from "./DepositByPlatform/DepositByPlatform";
import MonthlyTotals from "./MonthlyTotals/MonthlyTotals";
import FinancialsByState from "./FinancialsByState/FinancialsByState";

import {
  useGetFinancialsKpiCards,
  type FinancialsKpiCardsResponse,
} from "../../../queries/financials/useGetFinancialsKpiCards";

import { AppStrings } from "../../../utils/constants/AppStrings";
import { getAdminNow } from "../../../utils/dateService";
import {
  computeRedeemabilityCard,
  getRedeemabilityPctTitle,
} from "../../../utils/kpiMath";

import styles from "./FinancialsV2.module.css";
import { DATE_RANGE, KPI_CARD_BACKGROUND } from "../../../utils/Constants";
import {
  defaultDateRangeForViewport,
  defaultStartRangeForViewport,
} from "../../../utils/narrowViewportChartDates";
import { useKpiTabToolbar } from "../KpiTabToolbarContext";
import { useClientCountry } from "../../../hooks/useClientCountry";
import { useIsSweepstakesApplication } from "../../../hooks/useIsSweepstakesApplication";

const KpiCard = KpiCardJs as ComponentType<any>;
const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

const currentMonthRange = (): DateRange => {
  const now = getAdminNow();
  return [now.startOf("month"), now.endOf("day")];
};

interface KpiItem {
  title: string;
  /**
   * `null` for frontend-derived cards that don't map to a single BE
   * field (e.g. `redeemability_pct` is computed from deposit/withdrawal
   * amounts). The render path special-cases `null` keys so they bypass
   * the `kpiData[key]` lookup.
   */
  key: keyof FinancialsKpiCardsResponse | null;
  rate?: boolean;
  maximumFractionDigits?: number;
  titleTooltip?: string;
}

interface FinancialsV2Props {
  currencySymbol?: string;
}

// Order mirrors the Overview page financials section so admins see the same
// card sequence on both screens. `redeemability_pct` is the computed
// "withdrawals / deposits" ratio that replaces `active_deposit_users` here
// (the latter moved to the Acquisition section per PAMENG cleanup).
const KPI_CARD_CONFIG: KpiItem[] = [
  { title: "DEPOSIT AMOUNT", key: "deposit_amount" },
  { title: "WITHDRAWAL AMOUNT", key: "withdrawal_amount" },
  { title: "REVENUE", key: "revenue" },
  { title: "PROFITABILITY %", key: "profitability_pct", rate: true },
  {
    title: "TOTAL CASH BALANCE",
    key: "total_cash_balance",
    titleTooltip: AppStrings.OVERVIEW_TOTAL_CASH_BALANCE_TOOLTIP,
  },
  {
    title: "NON-WAGERED CASH",
    key: "non_wagered_cash",
    titleTooltip: AppStrings.OVERVIEW_NON_WAGERED_CASH_TOOLTIP,
  },
  {
    title: "REDEEMABLE CASH",
    key: "redeemable_cash",
    titleTooltip: AppStrings.OVERVIEW_REDEEMABLE_CASH_TOOLTIP,
  },
  {
    title: AppStrings.OVERVIEW_REDEEMABILITY_PCT,
    key: null,
    rate: true,
  },
];

const FinancialsV2: React.FC<FinancialsV2Props> = ({ currencySymbol }) => {
  // TCM-driven country code → drives the FinancialsByState geo map.
  const clientCountry = useClientCountry();
  const isSweepstakes = useIsSweepstakesApplication();
  const redeemabilityTitle = getRedeemabilityPctTitle(isSweepstakes);

  const [dates, setDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(currentMonthRange),
  );
  const [startRange] = useState(() =>
    defaultStartRangeForViewport(DATE_RANGE.CURRENT_MONTH),
  );

  const filters = useMemo(
    () => ({
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    }),
    [dates],
  );

  const { data: kpiData, isLoading, isError } = useGetFinancialsKpiCards(filters);

  const getData = useCallback(() => {}, []);

  const kpiCardConfig = useMemo(
    () =>
      KPI_CARD_CONFIG.map((item) =>
        item.key === null ? { ...item, title: redeemabilityTitle } : item,
      ),
    [redeemabilityTitle],
  );

  // Single source of truth for both the visible cards and the export
  // payload. Computed cards (key === null) get their values here so the
  // CSV row matches what's on screen verbatim.
  const cardValues = useMemo(() => {
    const redeemability = computeRedeemabilityCard(kpiData);
    return kpiCardConfig.map((item) => {
      if (item.key === null) {
        return { item, card: redeemability };
      }
      return { item, card: kpiData?.[item.key] ?? null };
    });
  }, [kpiData, kpiCardConfig]);

  const exportRows = useMemo(
    () =>
      cardValues.map(({ item, card }) => ({
        metric: item.title,
        value: card?.value ?? 0,
        change_pct: card?.percent_change ?? 0,
      })),
    [cardValues],
  );

  // Portal date picker + export into the shared KPI tabs row so they
  // wrap below the tab strip on narrow viewports rather than overlap it.
  const tabToolbar = useMemo(
    () => (
      <>
        <CustomDatePicker
          getData={getData}
          dates={dates}
          setDates={setDates}
          showPresets={true}
          startRange={startRange}
        />
        <ExportToXlsxButton data={exportRows} filename="financials.xlsx" />
      </>
    ),
    [dates, exportRows, getData, startRange],
  );

  useKpiTabToolbar(tabToolbar);

  return (
    <div className={styles.financialsV2}>
      {isError && (
        <div className={styles.errorBanner} data-testid="kpi-error-banner">
          Failed to load KPI data. Please try again later.
        </div>
      )}

      <div className={styles.kpiGrid} data-testid="financials-kpi-grid">
        {cardValues.map(({ item, card }) => (
          <KpiCard
            key={item.title}
            isApiRunning={isLoading}
            title={item.title}
            titleTooltip={item.titleTooltip}
            value={card?.value ?? 0}
            percentage={card?.percent_change ?? 0}
            rate={item.rate ?? false}
            maximumFractionDigits={item.maximumFractionDigits ?? 2}
            currencySymbol={currencySymbol}
            hugeValue={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
        ))}
      </div>

      <DailyPerformance />

      <div className={styles.chartRow}>
        <RedeemableBalance />
        <DepositByPlatform
          startDate={filters.start_date}
          endDate={filters.end_date}
          isApiRunning={isLoading}
        />
      </div>

      <FinancialsByState country={clientCountry} />

      <MonthlyTotals />
    </div>
  );
};

export default FinancialsV2;
